import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalConfirmation } from '@/components/ModalConfirmation';
import {
  STRIPE_ORG_SETTINGS_QUERY_KEY,
  useDeclineStripeTerms,
  useGetStripeOrgSettings,
  useSetStripePaymentsEnabled,
} from '@/components/OutletPaymentsSection';
import { useHeroToast } from '@/hooks';
import { useOrganizationState } from '@/state';
import { StripeTermsModal } from '@/views/Settings/components';

/**
 * Prośba o akceptację NOWEJ wersji regulaminu płatności, pokazywana po zalogowaniu.
 *
 * POKAZUJE SIĘ WYŁĄCZNIE PRZY ZMIANIE WERSJI. Warunkiem jest zgoda, która już istnieje
 * i dotyczy innego brzmienia (`termsOutdated` = są zapisane warunki i ich wersja różni się
 * od obowiązującej). Hotel, który nigdy nic nie akceptował, tego okna NIE dostaje: on nie
 * ma czego potwierdzać, a płatności włącza świadomie w ustawieniach, razem z regulaminem.
 *
 * ODMOWA COŚ ROBI, nie tylko zamyka okno. Płatności zostają wyłączone, a punkty odpięte
 * od kont — inaczej gość płaciłby na warunkach, których obiekt właśnie nie przyjął.
 * Dlatego między „Anuluj" a tą operacją stoi jeszcze jedno pytanie: skutek jest widoczny
 * dla gościa natychmiast, a przywrócenie wymaga ręcznego wpięcia każdego punktu.
 */
export const StripeTermsGate: FC = () => {
  const { t } = useTranslation();
  const { organizationId, stripePaymentsAvailable } = useOrganizationState();
  const { showError, showSuccess } = useHeroToast();
  const queryClient = useQueryClient();

  const { data: settings, refetch } = useGetStripeOrgSettings(organizationId, stripePaymentsAvailable);
  const { mutate: setEnabled, isPending: isAccepting } = useSetStripePaymentsEnabled();
  const { mutate: decline, isPending: isDeclining } = useDeclineStripeTerms();

  const [isDeclineOpen, setDeclineOpen] = useState(false);

  /* Płatności muszą być WŁĄCZONE: przy wyłączonych nie ma czego gasić ani czym płacić,
     a hotel i tak zobaczy regulamin przy ponownym włączaniu. */
  const mustRenew = settings?.enabled === true && settings?.termsOutdated === true;

  const refresh = (): void => {
    queryClient.invalidateQueries({ queryKey: [STRIPE_ORG_SETTINGS_QUERY_KEY, organizationId] });
  };

  const handleAccept = (): void => {
    if (!organizationId) return;
    setEnabled({ hotelId: organizationId, enabled: true, acceptTerms: true }, {
      onSuccess: async () => {
        refresh();
        /* SPRAWDZAMY, CZY ZGODA NAPRAWDĘ SIĘ ZAPISAŁA. Samo 200 nie wystarcza: backend
           potrafi przyjąć żądanie i nie zapisać wersji (tak działał przed poprawką
           odnawiania zgód), a wtedy okno wracało w kółko bez słowa wyjaśnienia. Lepiej
           powiedzieć wprost, że zapis nie doszedł, niż udawać, że wszystko gra. */
        const fresh = await refetch();
        if (fresh.data?.termsOutdated) {
          showError(String(t(
            'settings.integrations.stripe.terms.notSaved',
            'Akceptacja nie zapisała się po stronie serwera. Odśwież stronę, a jeśli okno wróci, zgłoś to nam.',
          )));
          return;
        }
        showSuccess(String(t('settings.integrations.stripe.terms.accepted', 'Regulamin zaakceptowany')));
      },
      onError: () => showError(String(t(
        'settings.integrations.stripe.enableError',
        'Nie udało się zapisać akceptacji. Spróbuj ponownie.',
      ))),
    });
  };

  const handleDecline = (): void => {
    if (!organizationId) return;
    decline({ hotelId: organizationId }, {
      onSuccess: () => {
        setDeclineOpen(false);
        refresh();
        // Punkty i ich ustawienia płatności zmieniły się po stronie serwera — czyścimy
        // cały cache płatności, żeby ekrany outletów nie pokazywały nieaktualnych kont.
        queryClient.invalidateQueries({ queryKey: ['outlet-payments'] });
        showSuccess(String(t(
          'settings.integrations.stripe.terms.declined',
          'Płatności online zostały wyłączone, a punkty odpięte od kont.',
        )));
      },
      onError: () => showError(String(t(
        'settings.integrations.stripe.terms.declineError',
        'Nie udało się wyłączyć płatności. Spróbuj ponownie.',
      ))),
    });
  };

  if (!mustRenew) return null;

  return (
    <>
      <StripeTermsModal
        isOpen={!isDeclineOpen}
        onClose={() => setDeclineOpen(true)}
        onAccept={handleAccept}
        isSaving={isAccepting}
        termsVersion={settings?.currentTermsVersion}
        isRenewal
      />

      <ModalConfirmation
        isOpen={isDeclineOpen}
        onClose={() => setDeclineOpen(false)}
        onConfirm={handleDecline}
        isLoading={isDeclining}
        title={String(t('settings.integrations.stripe.terms.declineTitle', 'Nie akceptujesz nowych warunków?'))}
        description={String(t(
          'settings.integrations.stripe.terms.declineDescription',
          'Wyłączymy płatności online i odepniemy wszystkie punkty od kont Stripe. Goście przestaną '
          + 'płacić w aplikacji od razu. Konta zostaną przypisane do obiektu, więc po akceptacji '
          + 'wystarczy wpiąć punkty ponownie.',
        ))}
        buttonConfirm={String(t('settings.integrations.stripe.terms.declineConfirm', 'Wyłącz płatności'))}
      />
    </>
  );
};
